'use client';

import { motion } from 'framer-motion';
import { getSectionMotionHeading } from '@/components/dynamic-zone/section-motion-heading';
import { cn } from '@/lib/utils';
import { components } from '@/types/strapi';

export default function TwoColumnIconFeatures({
	heading = 'Why Are Bulk SMS Effective?',
	features = [
		{
			icon: '💬', // Using emoji instead of SVG
			title: 'Instant Reach & High Open Rates',
			description:
				'Bulk SMS campaigns, like those offered by Mtalkz, empower you to send bulk text messages directly to your audience, ensuring instant reach. With most messages opened within 90 seconds and an impressive 98% open rate—our bulk SMS platforms guarantee your content is seen quickly.',
		},
		{
			icon: '🔌', // Using emoji instead of SVG
			title: 'Seamless Integration & Reliable Delivery',
			description:
				"Whether you're using our SMS API for seamless integration or leveraging our Enterprise SMS gateway API for high-volume messaging, Mtalkz provides the tools and expertise to ensure your campaigns are effective. As a leading bulk SMS service provider, we offer reliable SMS services designed to enhance engagement and deliver results.",
		},
	],
	backgroundColor = 'bg-white',
	isFirst = false,
}: components['schemas']['DynamicZoneTwoColumnIconFeaturesComponent'] & {
	isFirst?: boolean;
}) {
	const SectionHeading = getSectionMotionHeading(isFirst);
	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				staggerChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: {
			opacity: 0,
			y: 20,
		},
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				type: 'spring',
				stiffness: 100,
				damping: 15,
			},
		},
	};

	return (
		<section
			data-testid="two-column-icon-features"
			className={cn(
				'two-column-icon-features-section',
				backgroundColor,
				'px-4 py-16 sm:px-6 lg:px-8',
			)}>
			<noscript>
				<style>{`
					.two-column-icon-features-section h2[style],
					.two-column-icon-features-section h3[style],
					.two-column-icon-features-section p[style],
					.two-column-icon-features-section div[style*='opacity'],
					.two-column-icon-features-section div[style*='scale'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<div className="mx-auto max-w-7xl">
				{heading && (
					<SectionHeading
						className="mb-8 text-center text-3xl font-bold text-gray-900 sm:text-4xl lg:text-5xl"
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}>
						{heading}
					</SectionHeading>
				)}

				<motion.div
					className="grid grid-cols-1 gap-8 md:gap-12 lg:grid-cols-2 lg:gap-16"
					variants={containerVariants}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}>
					{features.map((feature, index) => (
						<motion.div
							key={index}
							variants={itemVariants}
							className="group relative overflow-hidden rounded-2xl bg-gradient-to-br from-white to-gray-50 p-8 shadow-md transition-all duration-300 hover:shadow-xl">
							<div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-indigo-50/80 transition-transform duration-500 ease-out group-hover:scale-110" />
							<div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-blue-50/50 transition-transform duration-500 ease-out group-hover:scale-110" />

							<div className="relative space-y-6">
								{/* Icon and title group */}
								<div className="flex items-center space-x-4">
									<div className="relative shrink-0">
										<div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-600/10 to-blue-600/5 shadow-sm transition-transform duration-300 group-hover:scale-110">
											<span className="text-3xl transition-transform duration-300 group-hover:scale-110">
												{feature.icon}
											</span>
										</div>
										<div className="absolute -bottom-2 -right-2 h-5 w-5 rounded-full bg-indigo-100" />
										<div className="absolute -left-2 -top-2 h-4 w-4 rounded-full bg-blue-100" />
									</div>

									<div className="text-xl font-semibold text-gray-900 transition-colors duration-200 group-hover:text-indigo-600 sm:text-2xl">
										{feature.title}
									</div>
								</div>

								{/* Description group */}
								<div className="space-y-4">
									{feature.description && (
										<div
											className="text-base leading-relaxed text-gray-600 sm:text-lg"
											dangerouslySetInnerHTML={{
												__html: feature.description,
											}}
										/>
									)}
									<div>
										<motion.div
											className="cursor-pointer text-sm font-medium text-indigo-600 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
											initial={{ opacity: 0 }}
											whileInView={{ opacity: 0 }}
											whileHover={{ opacity: 1 }}>
											Learn more →
										</motion.div>
									</div>
								</div>
							</div>
						</motion.div>
					))}
				</motion.div>
			</div>
		</section>
	);
}

// export type { TwoColumnIconFeaturesProps };
